/**
 * Datadog Logging & RUM Utility for Frontend
 * Automatically includes user email in every log
 * 
 * Usage: 
 * 1. Initialize in App.js: initializeDatadogLogs()
 * 2. After login: setDatadogUserContext(user)
 * 3. All logs automatically include email
 */

import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';

const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const MAX_STRING_LENGTH = 240;
const MAX_STACK_LENGTH = 1200;
const MAX_ARRAY_ITEMS = 8;
const MAX_OBJECT_KEYS = 20;
const MAX_LOG_SERIALIZED_CHARS = 10000;
const MAX_PAYLOAD_PREVIEW_KEYS = 8;
const MAX_PAYLOAD_PREVIEW_ARRAY_ITEMS = 3;
const MAX_SANITIZE_DEPTH = 2;
const REDACTED_VALUE = '[REDACTED]';

const SENSITIVE_KEY_PATTERN =
  /(password|passcode|token|authorization|jwt|cookie|secret|otp|card|cvv|iban|ssn|email|phone)/i;
const BODY_FIELD_KEYS = new Set([
  'request_body',
  'response_body',
  'request_params',
  'query_params',
  'payload',
  'data',
  'response',
  'context',
]);
const PAYLOAD_PRIORITY_KEYS = new Set([
  'status',
  'status_code',
  'message',
  'detail',
  'error',
  'error_code',
  'code',
  'api_name',
  'url',
  'method',
  'id',
  'user_id',
  'booking_id',
  'checkout_session_id',
  'stripe_checkout_session_id',
  'stripe_subscription_id',
  'plan_id',
  'result',
]);
const PUBLIC_TEACHER_LIST_PATH_PATTERN = /(?:\/api)?\/get_all_teachers\/?$/i;
const PUBLIC_TUTOR_AVAILABILITY_PATH_PATTERN =
  /(?:\/api)?\/tutors\/[^/]+\/availability\/?$/i;
const TEACHER_PROFILE_PATH_PATTERN = /(?:\/api)?\/profile\/?$/i;

const isPlainObject = (value) =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const truncateString = (value, maxLength = MAX_STRING_LENGTH) => {
  const normalized = String(value ?? '');
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 15))}[TRUNCATED]`;
};

const stripQueryString = (value) => {
  const normalized = String(value ?? '');
  const queryIndex = normalized.indexOf('?');
  return queryIndex >= 0 ? normalized.slice(0, queryIndex) : normalized;
};

const matchesPublicTeacherListPath = (value = '') => {
  const normalized = stripQueryString(value);
  return PUBLIC_TEACHER_LIST_PATH_PATTERN.test(normalized);
};

const matchesPublicTutorAvailabilityPath = (value = '') => {
  const normalized = stripQueryString(value);
  return PUBLIC_TUTOR_AVAILABILITY_PATH_PATTERN.test(normalized);
};

const matchesTeacherProfilePath = (value = '') => {
  const normalized = stripQueryString(value);
  return TEACHER_PROFILE_PATH_PATTERN.test(normalized);
};

const matchesSuppressedPublicTutorPath = (value = '') =>
  matchesPublicTeacherListPath(value) || matchesPublicTutorAvailabilityPath(value);

const shouldCompactPublicTutorReadPayload = (apiName = '', url = '', authScope = 'public') => {
  if (authScope !== 'public') return false;

  const normalizedUrl = stripQueryString(url);
  return (
    apiName === 'get_all_teachers' ||
    matchesPublicTeacherListPath(normalizedUrl) ||
    matchesPublicTutorAvailabilityPath(normalizedUrl)
  );
};

const isExpectedTeacherSessionExpiry = ({
  url = '',
  statusCode = 0,
  errorMessage = '',
  authScope = 'public',
} = {}) => {
  if (authScope !== 'teacher') return false;

  const normalizedUrl = stripQueryString(url);
  const normalizedMessage = String(errorMessage || '').toLowerCase();
  const numericStatusCode = Number(statusCode);

  return (
    normalizedUrl === '/profile/' &&
    (numericStatusCode === 401 || numericStatusCode === 403) &&
    (
      normalizedMessage.includes('session has expired') ||
      normalizedMessage.includes('session expired') ||
      normalizedMessage.includes('log in again') ||
      normalizedMessage.includes('login again')
    )
  );
};

const hasSessionExpiryText = (value = '') => {
  const normalizedValue = String(value || '').toLowerCase();
  return (
    normalizedValue.includes('session has expired') ||
    normalizedValue.includes('session expired') ||
    normalizedValue.includes('log in again') ||
    normalizedValue.includes('login again') ||
    normalizedValue.includes('jwt token is not valid')
  );
};

const shouldSkipExpectedSessionExpiryLog = ({
  event = '',
  flow = '',
  metadata = {},
} = {}) => {
  if (!isPlainObject(metadata)) return false;

  const normalizedEvent = String(event || '');
  const normalizedFlow = String(flow || '');
  const normalizedUrl = stripQueryString(
    metadata.url || metadata.http?.url || metadata.context?.url || ''
  );
  const statusCode = Number(
    metadata.status_code ?? metadata.status ?? metadata.http?.status_code ?? 0
  );
  const messageCandidates = [
    metadata.error_message,
    metadata.message,
    metadata.detail,
    metadata.response_body?.detail,
    metadata.response_body?.message,
  ];
  const hasExpiryMessage = messageCandidates.some((candidate) =>
    hasSessionExpiryText(candidate)
  );
  const requiresLogout = Boolean(metadata.requires_logout);

  if (
    isExpectedTeacherSessionExpiry({
      url: normalizedUrl,
      statusCode,
      errorMessage: messageCandidates.find(Boolean) || '',
      authScope: metadata.auth_scope || '',
    })
  ) {
    return true;
  }

  if (!hasExpiryMessage && !requiresLogout) return false;

  const isProtectedAppApiLog =
    normalizedFlow === 'api' ||
    normalizedFlow === 'login' ||
    normalizedFlow === 'parent_login' ||
    normalizedEvent === 'login_api_result' ||
    normalizedEvent === 'parent_login_api_result' ||
    normalizedEvent === 'api_error' ||
    normalizedEvent === 'profile_completion_result';

  if (!isProtectedAppApiLog) return false;

  if (requiresLogout) return true;

  return [304, 401, 403].includes(statusCode);
};

const shouldIgnorePublicTeacherNetworkLog = (log = {}) => {
  const message = String(log?.message || '').toLowerCase();
  const httpUrl = String(log?.http?.url || '');
  const contextUrl = String(log?.context?.url || '');
  const logDataUrl = String(log?.url || '');
  const statusCode = Number(
    log?.http?.status_code ??
      log?.context?.status_code ??
      log?.context?.status ??
      log?.status_code
  );
  const isStatusZero = Number.isFinite(statusCode) ? statusCode === 0 : false;
  const isSuppressedTutorPath =
    matchesSuppressedPublicTutorPath(httpUrl) ||
    matchesSuppressedPublicTutorPath(contextUrl) ||
    matchesSuppressedPublicTutorPath(logDataUrl) ||
    matchesTeacherProfilePath(httpUrl) ||
    matchesTeacherProfilePath(contextUrl) ||
    matchesTeacherProfilePath(logDataUrl) ||
    message.includes('/api/get_all_teachers/') ||
    message.includes('/api/profile/') ||
    (message.includes('/api/tutors/') && message.includes('/availability/'));
  const isNetworkStyleMessage =
    message.includes('xhr error get') ||
    message.includes('failed to load') ||
    message.includes('network error') ||
    message.includes('mixed content');

  return isSuppressedTutorPath && (isStatusZero || isNetworkStyleMessage);
};

const shouldIgnorePublicTeacherNetworkRumEvent = (event = {}) => {
  const resourceUrl = String(event?.resource?.url || '');
  const viewUrl = String(event?.view?.url || '');
  const errorMessage = String(event?.error?.message || '').toLowerCase();
  const statusCode = Number(
    event?.resource?.status_code ??
      event?.http?.status_code ??
      event?.error?.resource?.status_code
  );
  const isStatusZero = Number.isFinite(statusCode) ? statusCode === 0 : false;
  const isSuppressedTutorPath =
    matchesSuppressedPublicTutorPath(resourceUrl) ||
    matchesSuppressedPublicTutorPath(viewUrl) ||
    matchesTeacherProfilePath(resourceUrl) ||
    matchesTeacherProfilePath(viewUrl) ||
    errorMessage.includes('/api/get_all_teachers/') ||
    errorMessage.includes('/api/profile/') ||
    (errorMessage.includes('/api/tutors/') &&
      errorMessage.includes('/availability/'));
  const isNetworkStyleMessage =
    errorMessage.includes('xhr error get') ||
    errorMessage.includes('failed to load') ||
    errorMessage.includes('network error') ||
    errorMessage.includes('mixed content');

  return isSuppressedTutorPath && (isStatusZero || isNetworkStyleMessage);
};

const parseSampleRate = (rawValue, fallback) => {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) return fallback;
  if (parsed < 0) return 0;
  if (parsed > 100) return 100;
  return parsed;
};

const LOGS_SESSION_SAMPLE_RATE = parseSampleRate(
  process.env.REACT_APP_DATADOG_LOGS_SESSION_SAMPLE_RATE,
  IS_PRODUCTION ? 40 : 100
);
const RUM_SESSION_SAMPLE_RATE = parseSampleRate(
  process.env.REACT_APP_DATADOG_RUM_SESSION_SAMPLE_RATE,
  IS_PRODUCTION ? 25 : 100
);
const RUM_REPLAY_SAMPLE_RATE = parseSampleRate(
  process.env.REACT_APP_DATADOG_RUM_REPLAY_SAMPLE_RATE,
  IS_PRODUCTION ? 10 : 20
);

const safeJsonLength = (value) => {
  try {
    return JSON.stringify(value).length;
  } catch (_error) {
    return Number.MAX_SAFE_INTEGER;
  }
};

const sanitizeScalar = (value, key = '') => {
  if (SENSITIVE_KEY_PATTERN.test(String(key || ''))) return REDACTED_VALUE;

  if (
    value === null ||
    typeof value === 'undefined' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  return truncateString(value, MAX_STRING_LENGTH);
};

const sanitizeValue = (value, key = '', depth = 0) => {
  if (SENSITIVE_KEY_PATTERN.test(String(key || ''))) return REDACTED_VALUE;
  if (value === null || typeof value === 'undefined') return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return sanitizeScalar(value, key);
  }

  if (depth >= MAX_SANITIZE_DEPTH) {
    if (Array.isArray(value)) {
      return { type: 'array', length: value.length };
    }
    if (isPlainObject(value)) {
      return { type: 'object', keys: Object.keys(value).slice(0, MAX_PAYLOAD_PREVIEW_KEYS) };
    }
    return truncateString(String(value), MAX_STRING_LENGTH);
  }

  if (Array.isArray(value)) {
    const preview = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item, index) => sanitizeValue(item, `${key}[${index}]`, depth + 1));

    if (value.length > MAX_ARRAY_ITEMS) {
      preview.push('[TRUNCATED_ARRAY]');
    }

    return preview;
  }

  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    const sanitizedObject = {};

    keys.slice(0, MAX_OBJECT_KEYS).forEach((childKey) => {
      const childValue = value[childKey];
      if (childValue === null || typeof childValue === 'undefined' || childValue === '') return;
      sanitizedObject[childKey] = sanitizeValue(childValue, childKey, depth + 1);
    });

    if (keys.length > MAX_OBJECT_KEYS) {
      sanitizedObject._truncated_keys = keys.length - MAX_OBJECT_KEYS;
    }

    return sanitizedObject;
  }

  return truncateString(String(value), MAX_STRING_LENGTH);
};

const summarizePayload = (payload, fieldName = '') => {
  if (payload === null || typeof payload === 'undefined') return payload;

  if (
    typeof payload === 'string' ||
    typeof payload === 'number' ||
    typeof payload === 'boolean'
  ) {
    return sanitizeScalar(payload, fieldName);
  }

  if (Array.isArray(payload)) {
    return {
      type: 'array',
      length: payload.length,
      preview: payload
        .slice(0, MAX_PAYLOAD_PREVIEW_ARRAY_ITEMS)
        .map((item, index) => sanitizeValue(item, `${fieldName}[${index}]`, 1)),
    };
  }

  if (!isPlainObject(payload)) {
    return truncateString(String(payload), MAX_STRING_LENGTH);
  }

  const payloadKeys = Object.keys(payload);
  const preview = {};
  let previewCount = 0;

  payloadKeys.forEach((childKey) => {
    if (previewCount >= MAX_PAYLOAD_PREVIEW_KEYS) return;

    const value = payload[childKey];
    if (value === null || typeof value === 'undefined' || value === '') return;

    const lowerKey = childKey.toLowerCase();
    const shouldPrioritize = PAYLOAD_PRIORITY_KEYS.has(lowerKey);
    const isSimpleValue =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

    if (!shouldPrioritize && !isSimpleValue) return;

    preview[childKey] = sanitizeValue(value, childKey, 1);
    previewCount += 1;
  });

  return {
    type: 'object',
    keys_count: payloadKeys.length,
    preview,
    truncated: payloadKeys.length > Object.keys(preview).length,
  };
};

const sanitizeMetadata = (metadata = {}) => {
  if (!isPlainObject(metadata)) return {};

  const sanitized = {};

  Object.entries(metadata).forEach(([key, value]) => {
    if (value === null || typeof value === 'undefined' || value === '') return;

    const lowerKey = String(key || '').toLowerCase();

    if (lowerKey.includes('url')) {
      sanitized[key] = stripQueryString(value);
      return;
    }

    if (lowerKey.includes('stack')) {
      sanitized[key] = truncateString(value, MAX_STACK_LENGTH);
      return;
    }

    if (BODY_FIELD_KEYS.has(lowerKey)) {
      sanitized[key] = summarizePayload(value, key);
      return;
    }

    sanitized[key] = sanitizeValue(value, key, 0);
  });

  return sanitized;
};

const removeEmptyFields = (value = {}) => {
  const nextValue = { ...value };
  Object.keys(nextValue).forEach((key) => {
    if (nextValue[key] === null || nextValue[key] === undefined || nextValue[key] === '') {
      delete nextValue[key];
    }
  });
  return nextValue;
};

const trimLogDataToLimit = (logData = {}) => {
  const normalizedData = { ...logData };
  const initialLength = safeJsonLength(normalizedData);

  if (initialLength <= MAX_LOG_SERIALIZED_CHARS) return removeEmptyFields(normalizedData);

  BODY_FIELD_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(normalizedData, key)) {
      normalizedData[key] = summarizePayload(normalizedData[key], key);
    }
  });

  if (typeof normalizedData.stack_trace === 'string') {
    normalizedData.stack_trace = truncateString(normalizedData.stack_trace, MAX_STACK_LENGTH);
  }

  normalizedData._payload_truncated = true;
  normalizedData._payload_size = initialLength;

  const normalizedLength = safeJsonLength(normalizedData);
  if (normalizedLength <= MAX_LOG_SERIALIZED_CHARS) {
    return removeEmptyFields(normalizedData);
  }

  const compactLogData = {
    event: normalizedData.event,
    level: normalizedData.level,
    flow: normalizedData.flow,
    step: normalizedData.step,
    user_id: normalizedData.user_id,
    email: normalizedData.email,
    journey_id: normalizedData.journey_id,
    session_id: normalizedData.session_id,
    platform: normalizedData.platform,
    timestamp: normalizedData.timestamp,
    api_name: normalizedData.api_name,
    url: normalizedData.url,
    method: normalizedData.method,
    status_code: normalizedData.status_code,
    error_message: normalizedData.error_message,
    error_code: normalizedData.error_code,
    is_timeout: normalizedData.is_timeout,
    is_network_error: normalizedData.is_network_error,
    _payload_truncated: true,
    _payload_size: normalizedLength,
    _compact_mode: true,
  };

  return removeEmptyFields(compactLogData);
};

export const sanitizeDatadogLogBeforeSend = (log = {}) => {
  if (!isPlainObject(log)) return false;
  if (shouldIgnorePublicTeacherNetworkLog(log)) return false;

  if (typeof log.message === 'string') {
    log.message = truncateString(log.message, MAX_STACK_LENGTH);
  }

  if (isPlainObject(log.http) && typeof log.http.url === 'string') {
    log.http.url = stripQueryString(log.http.url);
  }

  if (isPlainObject(log.view) && typeof log.view.url === 'string') {
    log.view.url = stripQueryString(log.view.url);
  }

  if (isPlainObject(log.error) && typeof log.error.stack === 'string') {
    log.error.stack = truncateString(log.error.stack, MAX_STACK_LENGTH);
  }

  if (isPlainObject(log.context)) {
    log.context = sanitizeMetadata(log.context);
  }

  return true;
};

export const sanitizeRumBeforeSend = (event = {}) => {
  if (!isPlainObject(event)) return false;
  if (shouldIgnorePublicTeacherNetworkRumEvent(event)) return false;

  if (isPlainObject(event.view) && typeof event.view.url === 'string') {
    event.view.url = stripQueryString(event.view.url);
  }

  if (isPlainObject(event.resource) && typeof event.resource.url === 'string') {
    event.resource.url = stripQueryString(event.resource.url);
  }

  if (isPlainObject(event.error) && typeof event.error.stack === 'string') {
    event.error.stack = truncateString(event.error.stack, MAX_STACK_LENGTH);
  }

  if (isPlainObject(event.context)) {
    event.context = sanitizeMetadata(event.context);
  }

  return true;
};

// ==================== INITIALIZATION ====================

// Initialize Datadog Logs and RUM
export const initializeDatadogLogs = () => {
  datadogLogs.init({
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN || 'your-client-token',
    site: process.env.REACT_APP_DATADOG_SITE || 'us3.datadoghq.com',
    service: 'mathdingo-web',
    env: process.env.NODE_ENV || 'development',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    forwardErrorsToLogs: true,
    sessionSampleRate: LOGS_SESSION_SAMPLE_RATE,
    beforeSend: sanitizeDatadogLogBeforeSend,
  });

  datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID || 'your-app-id',
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN || 'your-client-token',
    site: process.env.REACT_APP_DATADOG_SITE || 'us3.datadoghq.com',
    service: 'mathdingo-web',
    env: process.env.NODE_ENV || 'development',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    sessionSampleRate: RUM_SESSION_SAMPLE_RATE,
    sessionReplaySampleRate: RUM_REPLAY_SAMPLE_RATE,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: sanitizeRumBeforeSend,
  });

  if (RUM_REPLAY_SAMPLE_RATE > 0) {
    datadogRum.startSessionReplayRecording();
  }
};

// ==================== ID GENERATORS ====================

export const generateJourneyId = () => 
  `journey_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const generateSessionId = () => 
  `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// ==================== GLOBAL STATE ====================

let currentJourneyId = null;
let currentSessionId = generateSessionId();
let currentUserEmail = null; // EMAIL IS ALWAYS TRACKED GLOBALLY
let currentUserId = null;
let currentUserPlan = null;

// ==================== USER CONTEXT MANAGEMENT ====================

/**
 * Set user context - called on login/signup
 * EMAIL is automatically captured and used in all logs
 */
export const setDatadogUserContext = (user) => {
  if (user && user.email) {
    // Store email globally - IT WILL BE IN EVERY LOG
    currentUserEmail = user.email;
    currentUserId = user.id || user.user_id;
    currentUserPlan = user.plan_name || user.plan;

    // Set Datadog RUM user context
    datadogRum.setUser({
      id: user.id || user.user_id,
      name: user.name || user.user_name,
      email: user.email, // EMAIL IN RUM
      plan_name: user.plan_name || user.plan,
      is_paid: user.is_paid || user.isPaid || false,
      year: user.year,
      year_id: user.year_id,
    });

    // Set Datadog Logs user context
    datadogLogs.setUser({
      id: user.id || user.user_id,
      name: user.name || user.user_name,
      email: user.email, // EMAIL IN LOGS
      plan_name: user.plan_name || user.plan,
      is_paid: user.is_paid || user.isPaid || false,
      year: user.year,
      year_id: user.year_id,
    });

    // Log user context initialization with email in message
    logEvent({
      event: 'user_context_initialized',
      flow: 'auth',
      step: 'context_set',
      email: user.email,
      metadata: {
        user_id: user.id || user.user_id,
        plan_name: user.plan_name || user.plan,
      },
    });

    console.log(`✅ Datadog context set for: ${user.email}`);
  }
};

export const clearDatadogUserContext = () => {
  currentUserEmail = null;
  currentUserId = null;
  currentUserPlan = null;
  datadogRum.clearUser();
  datadogLogs.clearUser();
  console.log('✅ Datadog context cleared');
};

// ==================== JOURNEY MANAGEMENT ====================

export const startJourney = (flow) => {
  currentJourneyId = generateJourneyId();
  logEvent({
    event: 'journey_started',
    flow,
    step: 'started',
    metadata: { journey_id: currentJourneyId },
  });
  return currentJourneyId;
};

export const endJourney = (flow, result = 'completed') => {
  logEvent({
    event: 'journey_ended',
    flow,
    step: 'ended',
    metadata: { journey_id: currentJourneyId, result },
  });
  currentJourneyId = null;
};

export const getCurrentJourneyId = () => currentJourneyId;
export const getCurrentSessionId = () => currentSessionId;
export const getCurrentUserEmail = () => currentUserEmail;

// ==================== CORE LOGGING FUNCTION ====================

/**
 * Core log function - EMAIL IS ALWAYS INCLUDED
 */
export const logEvent = ({
  event,
  level = 'info',
  flow = null,
  step = null,
  user_id = null,
  email = null,
  journey_id = null,
  session_id = null,
  message = null,
  metadata = {},
}) => {
  if (shouldSkipExpectedSessionExpiryLog({ event, flow, metadata })) {
    return null;
  }

  // Ensure email is ALWAYS included if available
  const finalEmail = email || currentUserEmail;
  const sanitizedMetadata = sanitizeMetadata(metadata);
  
  const logData = trimLogDataToLimit({
    event,
    level,
    flow,
    step,
    user_id: user_id || currentUserId,
    email: finalEmail, // EMAIL ALWAYS INCLUDED
    journey_id: journey_id || currentJourneyId,
    session_id: session_id || currentSessionId,
    platform: 'web',
    timestamp: new Date().toISOString(),
    ...sanitizedMetadata,
  });

  // Create message with email for visibility
  const hasCustomMessage = Boolean(String(message || '').trim());
  const logMessage = String(message || event).trim() || event;
  const messageWithContext = finalEmail && !hasCustomMessage
    ? `${logMessage} [${finalEmail}]`
    : logMessage;

  // Log to Datadog
  if (level === 'error') {
    datadogLogs.logger.error(messageWithContext, logData);
  } else if (level === 'warn') {
    datadogLogs.logger.warn(messageWithContext, logData);
  } else {
    datadogLogs.logger.log(messageWithContext, logData, level);
  }

  // Development console logging
  if (process.env.NODE_ENV === 'development') {
    console.log(`[${level.toUpperCase()}] ${event}:`, logData);
  }

  return logData;
};

// ==================== SIGNUP FLOW LOGGERS ====================

export const logParentSignupAccountCreated = ({
  email = null,
  name = 'Parent',
} = {}) => {
  const userEmailText = email ? ` ${email}` : '';
  const parentSignupMessage = `TutorInfoPage :- User ${name}${userEmailText} clicked on the Create Parent Account To Create a parent account.`;

  logEvent({
    event: 'parent_account_created',
    flow: 'parent_signup',
    step: 'account_created',
    email,
    message: parentSignupMessage,
    metadata: {
      user_name: name,
      action: 'create_parent_account',
    },
  });
};

export const logTutorProfileClicked = ({
  email = null,
  user_name = 'Guest',
  user_id = null,
  tutor_id = null,
  tutor_name = 'Tutor',
  tutor_email = null,
  tutor_details = {},
} = {}) => {
  const userEmailText = email ? ` ${email}` : '';
  const tutorEmailText = tutor_email ? ` ${tutor_email}` : '';
  const profileClickMessage = `TutorInfoPage :-User ${user_name}${userEmailText} clicked on the Profile button for tutor ${tutor_name}${tutorEmailText}.`;

  logEvent({
    event: 'tutor_profile_clicked',
    flow: 'tutorinfo',
    step: 'profile_clicked',
    user_id,
    email,
    message: profileClickMessage,
    metadata: {
      user_name,
      tutor_id,
      tutor_name,
      tutor_email,
      tutor_details,
    },
  });
};

export const logTutorBookSessionClicked = ({
  email = null,
  user_name = 'Guest',
  user_id = null,
  tutor_id = null,
  tutor_name = 'Tutor',
  tutor_email = null,
  tutor_details = {},
} = {}) => {
  const userEmailText = email ? ` ${email}` : '';
  const tutorEmailText = tutor_email ? ` ${tutor_email}` : '';
  const bookSessionMessage = `TutorInfoPage :- User ${user_name}${userEmailText} clicked on the Book Session button for tutor ${tutor_name}${tutorEmailText}.`;

  logEvent({
    event: 'tutor_book_session_clicked',
    flow: 'tutorinfo',
    step: 'book_session_clicked',
    user_id,
    email,
    message: bookSessionMessage,
    metadata: {
      user_name,
      tutor_id,
      tutor_name,
      tutor_email,
      tutor_details,
    },
  });
};

export const logTutorConfirmPayClicked = ({
  email = null,
  user_name = 'Guest',
  user_id = null,
  tutor_id = null,
  tutor_name = 'Tutor',
  tutor_email = null,
  tutor_details = {},
} = {}) => {
  const userEmailText = email ? ` ${email}` : '';
  const tutorEmailText = tutor_email ? ` ${tutor_email}` : '';
  const confirmPayMessage = `TutorInfoPage :- ${user_name}${userEmailText} clicked on the confirm & pay button for tutor ${tutor_name}${tutorEmailText}.`;

  logEvent({
    event: 'tutor_confirm_pay_clicked',
    flow: 'tutorinfo',
    step: 'confirm_pay_clicked',
    user_id,
    email,
    message: confirmPayMessage,
    metadata: {
      user_name,
      tutor_id,
      tutor_name,
      tutor_email,
      tutor_details,
    },
  });
};

export const logTutorSuccessfullyBooked = ({
  email = null,
  user_name = 'Guest',
  user_id = null,
  tutor_id = null,
  tutor_name = 'Tutor',
  tutor_email = null,
  booking_id = null,
  tutor_details = {},
} = {}) => {
  const userEmailText = email ? ` ${email}` : '';
  const tutorEmailText = tutor_email ? ` ${tutor_email}` : '';
  const bookingSuccessMessage = `TutorInfoPage :- User ${user_name}${userEmailText} successfully booked tutor ${tutor_name}${tutorEmailText}.`;

  logEvent({
    event: 'tutor_successfully_booked',
    flow: 'tutorinfo',
    step: 'booking_successful',
    user_id,
    email,
    message: bookingSuccessMessage,
    metadata: {
      user_name,
      tutor_id,
      tutor_name,
      tutor_email,
      booking_id,
      tutor_details,
    },
  });
};

export const logSignupPageViewed = () => {
  logEvent({
    event: 'signup_page_viewed',
    flow: 'signup',
    step: 'page_viewed',
    email: currentUserEmail,
  });
};

export const logSignupSubmitted = (email, entry_point = 'web') => {
  logEvent({
    event: 'signup_submitted',
    flow: 'signup',
    step: 'submitted',
    email, // EMAIL INCLUDED
    metadata: { entry_point },
  });
};

export const logSignupApiResult = (email, status_code, request_body, response_body, error_message = null) => {
  logEvent({
    event: 'signup_api_result',
    level: error_message ? 'error' : 'info',
    flow: 'signup',
    step: 'api_result',
    email, // EMAIL INCLUDED
    metadata: {
      status_code,
      api_name: 'signup',
      url: '/api/signup',
      request_body,
      response_body,
      error_message,
    },
  });
};

export const logEmailVerificationSent = (email) => {
  logEvent({
    event: 'email_verification_sent',
    flow: 'signup',
    step: 'verification_sent',
    email, // EMAIL INCLUDED
  });
};

export const logEmailVerificationResult = (email, status, status_code, error_message = null) => {
  logEvent({
    event: 'email_verification_result',
    level: error_message ? 'error' : 'info',
    flow: 'signup',
    step: 'verification_result',
    email, // EMAIL INCLUDED
    metadata: { status, status_code, error_message },
  });
};

export const logEmailVerificationSuccess = (email, verification_id) => {
  logEvent({
    event: 'email_verification_success',
    flow: 'signup',
    step: 'verified',
    email, // EMAIL INCLUDED
    metadata: { verification_id },
  });
};

// ==================== ONBOARDING FLOW LOGGERS ====================

export const logProfileCompletionStarted = (user_id, email) => {
  logEvent({
    event: 'profile_completion_started',
    flow: 'onboarding',
    step: 'profile_started',
    user_id,
    email, // EMAIL INCLUDED
  });
};

export const logProfileCompletionResult = (user_id, email, status_code, request_body, response_body, error_message = null) => {
  logEvent({
    event: 'profile_completion_result',
    level: error_message ? 'error' : 'info',
    flow: 'onboarding',
    step: 'profile_completed',
    user_id,
    email, // EMAIL INCLUDED
    metadata: {
      status_code,
      api_name: 'profile_completion',
      request_body,
      response_body,
      error_message,
    },
  });
};

export const logOnboardingRoutingDecision = (user_id, email, from_step, to_step, reason) => {
  logEvent({
    event: 'onboarding_routing_decision',
    flow: 'onboarding',
    step: 'routing',
    user_id,
    email, // EMAIL INCLUDED
    metadata: { from_step, to_step, reason },
  });
};

// ==================== LOGIN FLOW LOGGERS ====================

export const logLoginPageViewed = () => {
  logEvent({
    event: 'login_page_viewed',
    flow: 'login',
    step: 'page_viewed',
    email: currentUserEmail,
  });
};

export const logLoginSubmitted = (email, login_method = 'email') => {
  logEvent({
    event: 'login_submitted',
    flow: 'login',
    step: 'submitted',
    email, // EMAIL INCLUDED
    metadata: { login_method },
  });
};

export const logParentLoginPageViewed = (redirect_to = null) => {
  logEvent({
    event: 'parent_login_page_viewed',
    flow: 'parent_login',
    step: 'page_viewed',
    email: currentUserEmail,
    metadata: { redirect_to },
  });
};

export const logParentLoginSubmitted = (email, redirect_to = null) => {
  logEvent({
    event: 'parent_login_submitted',
    flow: 'parent_login',
    step: 'submitted',
    email,
    metadata: { redirect_to },
  });
};

export const logParentLoginApiResult = (
  email,
  status_code,
  response_body,
  error_message = null
) => {
  logEvent({
    event: 'parent_login_api_result',
    level: error_message ? 'error' : 'info',
    flow: 'parent_login',
    step: 'api_result',
    email,
    metadata: {
      status_code,
      api_name: 'login',
      url: '/api/login',
      response_body,
      error_message,
    },
  });
};

export const logParentPostLoginRoutingDecision = (
  user_id,
  email,
  destination,
  reason,
  redirect_to = null
) => {
  logEvent({
    event: 'parent_post_login_routing_decision',
    flow: 'parent_login',
    step: 'routing',
    user_id,
    email,
    metadata: {
      destination,
      reason,
      redirect_to,
    },
  });
};

export const logLoginApiResult = (email, status_code, api_name, response_body, error_message = null) => {
  logEvent({
    event: 'login_api_result',
    level: error_message ? 'error' : 'info',
    flow: 'login',
    step: 'api_result',
    email, // EMAIL INCLUDED
    metadata: {
      status_code,
      api_name,
      url: `/api/${api_name}`,
      response_body,
      error_message,
    },
  });
};

export const logUserContextSet = (user_id, email, plan_name, is_paid, year_id) => {
  logEvent({
    event: 'user_context_set',
    flow: 'login',
    step: 'context_set',
    user_id,
    email, // EMAIL INCLUDED
    metadata: { plan_name, is_paid, year_id },
  });
};

export const logPostLoginRoutingDecision = (user_id, email, destination, reason) => {
  logEvent({
    event: 'post_login_routing_decision',
    flow: 'login',
    step: 'routing',
    user_id,
    email, // EMAIL INCLUDED
    metadata: { destination, reason },
  });
};

export const logLogoutAction = (email) => {
  logEvent({
    event: 'logout',
    flow: 'login',
    step: 'logout',
    email, // EMAIL INCLUDED
  });
};

// ==================== PURCHASE FLOW LOGGERS ====================

export const logPricingPageViewed = (email = null) => {
  logEvent({
    event: 'pricing_page_viewed',
    flow: 'purchase',
    step: 'pricing_viewed',
    email: email || currentUserEmail,
  });
};

export const logPlanSelected = (email, plan_id, plan_name, billing_interval, price) => {
  logEvent({
    event: 'plan_selected',
    flow: 'purchase',
    step: 'plan_selected',
    email, // EMAIL INCLUDED
    metadata: { plan_id, plan_name, billing_interval, price },
  });
};

export const logStripeCheckoutSessionCreateResult = (
  email,
  plan_id,
  status_code,
  stripe_checkout_session_id,
  request_body,
  response_body,
  error_message = null
) => {
  logEvent({
    event: 'stripe_checkout_session_create_result',
    level: error_message ? 'error' : 'info',
    flow: 'purchase',
    step: 'checkout_session_create',
    email, // EMAIL INCLUDED
    metadata: {
      plan_id,
      status_code,
      api_name: 'create_stripe_checkout_session',
      url: '/api/create_stripe_checkout_session',
      stripe_checkout_session_id,
      request_body,
      response_body,
      error_message,
    },
  });
};

export const logStripeRedirectStarted = (email, plan_id, stripe_checkout_session_id) => {
  logEvent({
    event: 'stripe_redirect_started',
    flow: 'purchase',
    step: 'redirect_started',
    email, // EMAIL INCLUDED
    metadata: { plan_id, stripe_checkout_session_id },
  });
};

export const logStripeReturnResult = (email, result, stripe_checkout_session_id, query_params = {}) => {
  logEvent({
    event: 'stripe_return_result',
    flow: 'purchase',
    step: 'return_result',
    email, // EMAIL INCLUDED
    metadata: { result, stripe_checkout_session_id, query_params },
  });
};

export const logPaymentJourneyFinalStatus = (
  email,
  result,
  failure_stage = null,
  plan_id = null,
  stripe_checkout_session_id = null,
  stripe_subscription_id = null
) => {
  logEvent({
    event: 'payment_journey_final_status',
    level: result === 'success' ? 'info' : 'error',
    flow: 'purchase',
    step: 'final_status',
    email, // EMAIL INCLUDED
    metadata: {
      result,
      failure_stage,
      plan_id,
      stripe_checkout_session_id,
      stripe_subscription_id,
    },
  });
};

export const logBookingApiResult = ({
  api_name,
  url,
  status_code,
  request_body = null,
  response_body = null,
  error_message = null,
  error_code = null,
  is_timeout = false,
  is_network_error = false,
  booking_id = null,
  checkout_session_id = null,
  booking_source = null,
}) => {
  logEvent({
    event: 'booking_api_result',
    level: error_message ? 'error' : 'info',
    flow: 'class_booking',
    step: 'api_result',
    email: currentUserEmail,
    metadata: {
      api_name,
      url,
      status_code,
      request_body,
      response_body,
      error_message,
      error_code,
      is_timeout,
      is_network_error,
      booking_id,
      checkout_session_id,
      booking_source,
    },
  });
};

export const logTeacherApiResult = ({
  api_name,
  url,
  method,
  status_code,
  request_body = null,
  query_params = null,
  response_body = null,
  error_message = null,
  error_code = null,
  is_timeout = false,
  is_network_error = false,
  auth_scope = "public",
}) => {
  // Teacher API result logs are intentionally disabled to avoid noisy tutor
  // browsing events appearing in Datadog for parent and public flows.
  void api_name;
  void url;
  void method;
  void status_code;
  void request_body;
  void query_params;
  void response_body;
  void error_message;
  void error_code;
  void is_timeout;
  void is_network_error;
  void auth_scope;
  return null;
};

// ==================== DASHBOARD LOGGERS ====================

export const logDashboardLoaded = (source) => {
  logEvent({
    event: 'dashboard_loaded',
    flow: 'dashboard',
    step: 'loaded',
    email: currentUserEmail,
    metadata: { source },
  });
};

export const logDashboardLoadBlocked = (reason, expected_source) => {
  logEvent({
    event: 'dashboard_load_blocked',
    level: 'error',
    flow: 'dashboard',
    step: 'load_blocked',
    email: currentUserEmail,
    metadata: { reason, expected_source },
  });
};

export const logDashboardAction = (action, details = {}) => {
  logEvent({
    event: `dashboard_${action}`,
    flow: 'dashboard',
    step: action,
    email: currentUserEmail,
    metadata: details,
  });
};

// ==================== ERROR LOGGERS ====================

export const logError = (error_name, error_message, stack_trace = null, context = {}) => {
  logEvent({
    event: 'frontend_error',
    level: 'error',
    email: currentUserEmail,
    metadata: {
      error_name,
      error_message,
      stack_trace,
      ...context,
    },
  });
};

export const logApiError = (api_name, status_code, error_message, response_body = null) => {
  logEvent({
    event: 'api_error',
    level: 'error',
    email: currentUserEmail,
    metadata: {
      api_name,
      status_code,
      error_message,
      response_body,
    },
  });
};

// ==================== PAYMENT CHECK LOGGERS ====================

export const logCheckStripeSubscriptionResult = (status_code, subscription_status, has_access, error_message = null) => {
  logEvent({
    event: 'check_stripe_subscription_result',
    level: error_message ? 'error' : 'info',
    email: currentUserEmail,
    metadata: {
      status_code,
      api_name: 'check_stripe_subscription',
      subscription_status,
      has_access,
      error_message,
    },
  });
};

// ==================== EXPORT SUMMARY ====================
export default {
  initializeDatadogLogs,
  setDatadogUserContext,
  clearDatadogUserContext,
  startJourney,
  endJourney,
  logEvent,
  logSignupPageViewed,
  logParentSignupAccountCreated,
  logTutorProfileClicked,
  logTutorBookSessionClicked,
  logTutorConfirmPayClicked,
  logTutorSuccessfullyBooked,
  logSignupSubmitted,
  logSignupApiResult,
  logEmailVerificationSent,
  logEmailVerificationResult,
  logEmailVerificationSuccess,
  logProfileCompletionStarted,
  logProfileCompletionResult,
  logLoginPageViewed,
  logLoginSubmitted,
  logParentLoginPageViewed,
  logParentLoginSubmitted,
  logParentLoginApiResult,
  logParentPostLoginRoutingDecision,
  logLoginApiResult,
  logUserContextSet,
  logPostLoginRoutingDecision,
  logLogoutAction,
  logPricingPageViewed,
  logPlanSelected,
  logStripeCheckoutSessionCreateResult,
  logStripeRedirectStarted,
  logStripeReturnResult,
  logPaymentJourneyFinalStatus,
  logBookingApiResult,
  logTeacherApiResult,
  logDashboardLoaded,
  logDashboardLoadBlocked,
  logDashboardAction,
  logError,
  logApiError,
  logCheckStripeSubscriptionResult,
  getCurrentUserEmail,
  getCurrentJourneyId,
  getCurrentSessionId,
};
